import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
// import modal
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Validation Schema
  const staffSchema = Yup.object().shape({
    staff_name: Yup.string().required('Name is required'),
    staff_email: Yup.string().email('Invalid email').required('Email is required'),
    staff_role: Yup.string().required('Role is required'),
    custom_fields: Yup.object().optional(),
  });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getStaff();
      setStaff(response.data);
    } catch (error) {
      toast.error('Failed to fetch staff data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editingStaff) {
        await adminAPI.updateStaff(editingStaff.id, values);
        toast.success('Staff updated successfully');
      } else {
        await adminAPI.createStaff(values);
        toast.success('Staff created successfully');
      }
      
      setShowModal(false);
      resetForm();
      setEditingStaff(null);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await adminAPI.deleteStaff(id);
        toast.success('Staff deleted successfully');
        fetchStaff();
      } catch (error) {
        toast.error('Failed to delete staff');
      }
    }
  };

  const handleStatusChange = async (id, currentStatus) => {
    const newStatus = currentStatus === 'activated' ? 'deactivated' : 'activated';
    try {
      await adminAPI.updateStaffStatus(id, newStatus);
      toast.success(`Staff ${newStatus} successfully`);
      fetchStaff();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredStaff = staff.filter(staffMember =>
    staffMember.staff_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staffMember.staff_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staffMember.staff_role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const roleColors = {
      'lead_one': 'bg-purple-100 text-purple-800',
      'lead_two': 'bg-blue-100 text-blue-800',
      'lead_three': 'bg-green-100 text-green-800',
      'lead_four': 'bg-yellow-100 text-yellow-800',
      'lead_five': 'bg-gray-100 text-gray-800',
    };
    
    const roleNames = {
      'lead_one': 'Admin',
      'lead_two': 'Form Master',
      'lead_three': 'Admission Officer',
      'lead_four': 'Subject Teacher',
      'lead_five': 'Student',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${roleColors[role] || 'bg-gray-100 text-gray-800'}`}>
        {roleNames[role] || role}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'activated': 'bg-green-100 text-green-800',
      'deactivated': 'bg-red-100 text-red-800',
      'pending': 'bg-yellow-100 text-yellow-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Staff Management</h1>
          <p className="text-gray-600">Manage staff members and their roles</p>
        </div>
        <button
          onClick={() => {
            setEditingStaff(null);
            setShowModal(true);
          }}
          className="btn-primary flex items-center"
        >
          <i className="fas fa-plus mr-2"></i> Add Staff
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fas fa-search text-gray-400"></i>
              </div>
              <input
                type="text"
                placeholder="Search by name, email, or role"
                className="pl-10 input-field"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role Filter</label>
            <select className="input-field">
              <option value="">All Roles</option>
              <option value="lead_one">Administrator</option>
              <option value="lead_two">Form Master</option>
              <option value="lead_three">Admission Officer</option>
              <option value="lead_four">Subject Teacher</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status Filter</label>
            <select className="input-field">
              <option value="">All Status</option>
              <option value="activated">Active</option>
              <option value="deactivated">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Staff Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="table-responsive">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Staff Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStaff.map((staffMember) => (
                <tr key={staffMember.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <i className="fas fa-user text-primary-600"></i>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {staffMember.staff_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {staffMember.staff_email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getRoleBadge(staffMember.staff_role)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(staffMember.status)}
                      <button
                        onClick={() => handleStatusChange(staffMember.id, staffMember.status)}
                        className={`text-xs px-2 py-1 rounded ${
                          staffMember.status === 'activated'
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {staffMember.status === 'activated' ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(staffMember.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEdit(staffMember)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(staffMember.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredStaff.length === 0 && (
          <div className="text-center py-12">
            <i className="fas fa-users text-4xl text-gray-300 mb-4"></i>
            <p className="text-gray-500">No staff members found</p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingStaff(null);
        }}
        title={editingStaff ? 'Edit Staff Member' : 'Add New Staff'}
      >
        <Formik
          initialValues={{
            staff_name: editingStaff?.staff_name || '',
            staff_email: editingStaff?.staff_email || '',
            staff_role: editingStaff?.staff_role || 'lead_four',
            custom_fields: editingStaff?.custom_fields || {},
          }}
          validationSchema={staffSchema}
          onSubmit={handleCreateStaff}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <Field
                  type="text"
                  name="staff_name"
                  className="input-field"
                  placeholder="Enter full name"
                />
                {errors.staff_name && touched.staff_name && (
                  <div className="text-red-500 text-sm mt-1">{errors.staff_name}</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address (gmail only) *
                </label>
                <Field
                  type="email"
                  name="staff_email"
                  className="input-field"
                  placeholder="Enter email address"
                />
                {errors.staff_email && touched.staff_email && (
                  <div className="text-red-500 text-sm mt-1">{errors.staff_email}</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <Field as="select" name="staff_role" className="input-field">
                  <option value="lead_one">Administrator</option>
                  <option value="lead_two">Form Master</option>
                  {/* <option value="lead_three">Admission Officer</option> */}
                  <option value="lead_four">Subject Teacher</option>
                  {/* <option value="lead_five">Student</option> */}
                </Field>
                {errors.staff_role && touched.staff_role && (
                  <div className="text-red-500 text-sm mt-1">{errors.staff_role}</div>
                )}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Information (Optional)
                </label>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Phone Number</label>
                    <Field
                      type="text"
                      name="custom_fields.phone"
                      className="input-field"
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Department</label>
                    <Field
                      type="text"
                      name="custom_fields.department"
                      className="input-field"
                      placeholder="Enter department"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingStaff(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      {editingStaff ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default StaffManagement;